import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
} from "reactstrap";
// Using custom Bootstrap pagination for consistency
import { getDepartments, saveDepartment } from "../../components/Api/GmsDepartment";
import { getCompanies } from "../../components/Api/GmsCompany";
import { Eye, SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  getUserPermissions, 
  hasPagePermissionWithAdmin, 
  hasEditPermissionWithAdmin, 
  hasDeletePermissionWithAdmin 
} from "../../components/Permission/PermissionService";

const defaultForm = {
  department_Id: null,
  department_NameEn: "",
  department_NameAr: "",
  department_Company_Id: null,
  department_CreatedDate: new Date().toISOString(),
  department_CreatedBy: 1,
  department_ModifiedDate: new Date().toISOString(),
  department_ModifiedBy: 1,
};

const emptyFilter = {
  department_Id: null,
  department_Code: "",
  department_NameEn: "",
  department_NameAr: "",
  department_Company_Id: null,
};

export default function DepartmentPage() {
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  
  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "serialNumber",
        enableColumnFilter: false,
        cell: ({ row }) => row.index + 1,
      },
      {
        header: "Code",
        accessorKey: "department_Code",
        enableColumnFilter: false,
      },
      {
        header: "Name (En)",
        accessorKey: "department_NameEn",
        enableColumnFilter: false,
      },
      {
        header: "Name (Ar)",
        accessorKey: "department_NameAr",
        enableColumnFilter: false,
      },
      {
        header: "Group",
        accessorKey: "company_NameEn",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const dept = row.original;
          // If company_NameEn is already in the response, use it
          if (dept.company_NameEn) return dept.company_NameEn;
          // Otherwise find the company name from companies array
          const company = companies.find(c => c.company_Id === dept.department_Company_Id);
          return company ? company.company_NameEn : "N/A";
        },
      },
      {
        header: "View",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleView(row.original);
            }}
            className="btn btn-link p-0"
            disabled={viewLoadingRow === row.original.department_Id}
          >
            {viewLoadingRow === row.original.department_Id ? (
              <ClipLoader size={20} color="black" />
            ) : (
              <Eye size={20} color="black" />
            )}
          </button>
        ),
      },
      {
        header: "Edit",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          canEdit ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.original);
              }}
              className="btn btn-link p-0"
              disabled={editLoadingRow === row.original.department_Id}
            >
              {editLoadingRow === row.original.department_Id ? (
                <ClipLoader size={20} color="black" />
              ) : (
                <SquarePen size={20} color="black" />
              )}
            </button>
          ) : null
        ),
      },
    ],
    [viewLoadingRow, editLoadingRow, companies, canEdit]
  );

  // Load permissions first
  useEffect(() => {
    loadPermissions();
  }, []);

  // Load data after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadDepartments();
      loadCompanies();
    }
  }, [permissionsLoading, canView]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);
      
      // Check permissions for this page
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsDepartment');
      const hasEdit = hasEditPermissionWithAdmin(userPermissions, '/GmsDepartment');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsDepartment');
      
      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);
      
      // If no view permission, redirect to dashboard
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadDepartments = async (filterData = emptyFilter) => {
    setLoading(true);
    try {
      const data = await getDepartments(filterData);
      setDepartments(data);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load departments: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await getCompanies({});
      setCompanies(data);
    } catch (error) {
      Swal.fire("Error", "Failed to load companies: " + error.message, "error");
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Set default active status based on permissions
  useEffect(() => {
    if (!isEditing && canEdit && !canDelete) {
      setForm(prev => ({
        ...prev,
        department_Active: true
      }));
    }
  }, [isEditing, canEdit, canDelete]);

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Prepare save data - remove ID field for inserts
      const saveData = {
        ...form,
        department_ModifiedDate: new Date().toISOString(),
      };
      
      // Only include department_Id if it exists and is > 0 (update operation)
      if (saveData.department_Id && saveData.department_Id > 0) {
        // Keep the ID for updates
      } else {
        delete saveData.department_Id;
      }
      
      // Only include department_Company_Id if it exists and is > 0
      if (!saveData.department_Company_Id || saveData.department_Company_Id === 0) {
        delete saveData.department_Company_Id;
      }
      
      const result = await saveDepartment(saveData);
      Swal.fire("Success", result.message, "success");
      setForm({
        ...defaultForm,
        department_Company_Id: null
      });
      setIsEditing(false);
      loadDepartments(filter);
    } catch (error) {
      Swal.fire("Error", "Failed to save department: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (department) => {
    setViewLoadingRow(department.department_Id);
    try {
      // Find company name if not in department object
      let companyName = department.company_NameEn;
      if (!companyName) {
        const company = companies.find(c => c.company_Id === department.department_Company_Id);
        companyName = company ? company.company_NameEn : "N/A";
      }

      Swal.fire({
        title: department.department_NameEn,
        html: `
          <div class="text-left">
            <div class="mb-3">
              <h6 class="mb-2">Basic Information</h6>
              <p><strong>ID:</strong> ${department.department_Id}</p>
              <p><strong>Code:</strong> ${department.department_Code}</p>
              <p><strong>Name (English):</strong> ${department.department_NameEn}</p>
              <p><strong>Name (Arabic):</strong> ${department.department_NameAr}</p>
              <p><strong>Company:</strong> ${companyName}</p>
            </div>
            <div class="mb-3">
              <h6 class="mb-2">Timestamps</h6>
              <p><strong>Created Date:</strong> ${new Date(department.department_CreatedDate).toLocaleString()}</p>
              <p><strong>Last Modified:</strong> ${new Date(department.department_ModifiedDate).toLocaleString()}</p>
            </div>
          </div>
        `,
        icon: "info",
        width: '600px',
        customClass: {
          container: 'custom-swal-container',
          popup: 'custom-swal-popup',
          content: 'custom-swal-content'
        }
      });
    } catch (error) {
      Swal.fire("Error", "Failed to view department details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleEdit = (dept) => {
    setForm({
      ...dept,
      department_ModifiedDate: new Date().toISOString(),
    });
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm({
      ...defaultForm,
      department_Company_Id: 0
    });
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadDepartments(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadDepartments(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Get paginated data for display
  const getPaginatedDepartments = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return departments.slice(startIndex, endIndex);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return '-';
    }
  };

  // Options for searchable dropdowns
  const nameEnOptions = useMemo(() => 
    departments.map(dept => ({
      value: dept.department_NameEn,
      label: dept.department_NameEn
    }))
  , [departments]);

  const nameArOptions = useMemo(() => 
    departments.map(dept => ({
      value: dept.department_NameAr,
      label: dept.department_NameAr
    }))
  , [departments]);

  const companyOptions = useMemo(() => 
    companies.map(company => ({
      value: company.company_Id,
      label: company.company_NameEn
    }))
  , [companies]);

  const codeOptions = useMemo(() => 
    departments.map(dept => ({
      value: dept.department_Code,
      label: dept.department_Code
    }))
  , [departments]);

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody className="text-center py-5">
                  <ClipLoader size={50} color="#556ee6" />
                  <p className="mt-3">Loading permissions...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Department Management</h4>

                {/* Form Section - Only show if user has edit permission */}
                {canEdit && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-3">
                        {isEditing ? "Edit Department" : "Add New Department"}
                      </h5>
                    <Form onSubmit={handleSubmit}>
                      <Row>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Name (EN)</Label>
                            <Input
                              name="department_NameEn"
                              value={form.department_NameEn}
                              onChange={(e) => handleFormChange("department_NameEn", e.target.value)}
                              required
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Name (AR)</Label>
                            <Input
                              name="department_NameAr"
                              value={form.department_NameAr}
                              onChange={(e) => handleFormChange("department_NameAr", e.target.value)}
                              required
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Group</Label>
                            <Select
                              key={form.department_Company_Id || 'empty'}
                              value={companyOptions.find(
                                option => option.value === form.department_Company_Id
                              )}
                              onChange={(option) =>
                                handleFormChange("department_Company_Id", option ? option.value : null)
                              }
                              options={companyOptions}
                              placeholder="Select Group..."
                              isClearable
                              required
                            />
                          </div>
                        </Col>
                      </Row>

                      <div className="d-flex gap-2">
                        <Button
                          type="submit"
                          color="success"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <ClipLoader size={20} color="white" />{" "}
                              <span className="ms-2">Saving...</span>
                            </>
                          ) : isEditing ? (
                            "Update Department"
                          ) : (
                            "Save Department"
                          )}
                        </Button>
                        {isEditing && (
                          <Button
                            type="button"
                            color="secondary"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </Form>
                  </CardBody>
                </Card>
                )}

                {/* Filter Section */}
                <Card className="mb-4">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0">Filter Departments</h5>
                      <Button
                        color="primary"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </Button>
                    </div>

                    {showFilters && (
                      <>
                        <Row className="mb-3">
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Code</Label>
                              <Select
                                isClearable
                                value={
                                  filter.department_Code
                                    ? codeOptions.find(
                                        option => option.value === filter.department_Code
                                      )
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "department_Code",
                                    option ? option.value : ""
                                  )
                                }
                                options={codeOptions}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Name (EN)</Label>
                              <Select
                                isClearable
                                value={
                                  filter.department_NameEn
                                    ? nameEnOptions.find(
                                        option => option.value === filter.department_NameEn
                                      )
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "department_NameEn",
                                    option ? option.value : ""
                                  )
                                }
                                options={nameEnOptions}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Name (AR)</Label>
                              <Select
                                isClearable
                                value={
                                  filter.department_NameAr
                                    ? nameArOptions.find(
                                        option => option.value === filter.department_NameAr
                                      )
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "department_NameAr",
                                    option ? option.value : ""
                                  )
                                }
                                options={nameArOptions}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Group</Label>
                              <Select
                                isClearable
                                value={
                                  filter.department_Company_Id
                                    ? companyOptions.find(
                                        option => option.value === filter.department_Company_Id
                                      )
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "department_Company_Id",
                                    option ? option.value : null
                                  )
                                }
                                options={companyOptions}
                              />
                            </div>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2">
                          <Button color="primary" onClick={handleApplyFilter}>
                            Apply
                          </Button>
                          <Button color="warning" onClick={handleClearFilter}>
                            Clear
                          </Button>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>

                {/* Table Section */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      Department List ({totalCount} records)
                    </h5>
                    <div className="table-responsive">
                      <Table bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Sr.No</th>
                            <th>Code</th>
                            <th>Name (EN)</th>
                            <th>Name (AR)</th>
                            <th>Group</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {departments.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="text-center">
                                No departments found
                              </td>
                            </tr>
                          ) : (
                            getPaginatedDepartments().map((department, index) => (
                              <tr key={department.department_Id}>
                                <td>{(page - 1) * pageSize + index + 1}</td>
                                <td>{department.department_Code}</td>
                                <td>{department.department_NameEn}</td>
                                <td>{department.department_NameAr}</td>
                                <td>{companies.find(c => c.company_Id === department.department_Company_Id)?.company_NameEn || '-'}</td>

                                <td>
                                  <div className="d-flex gap-2">
                                    {canEdit && (
                                      <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => handleEdit(department)}
                                      >
                                        Edit
                                      </Button>
                                    )}
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handleView(department)}
                                    >
                                      View
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                         style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                      <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                        <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                        Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                      </div>
                      {totalCount > pageSize && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                              color: page === 1 ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-left"></i>
                          </Button>
                          
                          {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                            const pageNum = index + 1;
                            const isActive = page === pageNum;
                            
                            return (
                              <Button
                                key={pageNum}
                                color={isActive ? "primary" : "light"}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                  color: isActive ? '#fff' : '#495057',
                                  fontWeight: isActive ? '600' : '400',
                                  boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                }}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                          
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === Math.ceil(totalCount / pageSize)}
                            onClick={() => handlePageChange(page + 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                              color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-right"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
